import { DISTRICT_MAP_URL } from "../../../../utils/constants";
import React from "react";

const DISTRICT_MAP_WIDTH = 420;
const DISTRICT_MAP_HEIGHT = 260;
const DISTRICT_MAP_PADDING = 12;
const DISTRICT_FILL_COLOR = "#1e3a5f";
const DISTRICT_STROKE_COLOR = "#ffffff";
const DISTRICT_MAP_BASE_URL =
  DISTRICT_MAP_URL;

const DISTRICT_MAP_SLUG_BY_STATE = {
  "Dadra & Nagar Haveli and Daman & Diu": "dnh-and-dd",
};

const DISTRICT_NAME_KEYS = [
  "district",
  "District",
  "DISTRICT",
  "dtname",
  "DT_NAME",
  "distname",
  "DIST_NAME",
  "name",
  "NAME",
  "NAME_2",
];

const getDistrictMapSlug = (stateName) =>
  DISTRICT_MAP_SLUG_BY_STATE[stateName] ||
  stateName
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const getDistrictMapUrl = (stateName) =>
  `${DISTRICT_MAP_BASE_URL}/${getDistrictMapSlug(stateName)}.geojson`;

const collectCoordinatePairs = (coordinates) => {
  const points = [];

  const visit = (value) => {
    if (!Array.isArray(value)) return;

    if (typeof value[0] === "number" && typeof value[1] === "number") {
      points.push(value);
      return;
    }

    value.forEach(visit);
  };

  visit(coordinates);
  return points;
};

const getGeometryPoints = (geometry) => {
  if (!geometry) return [];

  if (geometry.type === "GeometryCollection") {
    return (geometry.geometries || []).flatMap(getGeometryPoints);
  }

  return collectCoordinatePairs(geometry.coordinates);
};

const getDistrictName = (properties = {}, index) => {
  const nameKey = DISTRICT_NAME_KEYS.find((key) => properties[key]);

  return nameKey ? properties[nameKey] : `District ${index + 1}`;
};

const getProjection = (points) => {
  const bounds = points.reduce(
    (currentBounds, [longitude, latitude]) => ({
      minLongitude: Math.min(currentBounds.minLongitude, longitude),
      maxLongitude: Math.max(currentBounds.maxLongitude, longitude),
      minLatitude: Math.min(currentBounds.minLatitude, latitude),
      maxLatitude: Math.max(currentBounds.maxLatitude, latitude),
    }),
    {
      minLongitude: Infinity,
      maxLongitude: -Infinity,
      minLatitude: Infinity,
      maxLatitude: -Infinity,
    }
  );

  const longitudeSpan = bounds.maxLongitude - bounds.minLongitude || 1;
  const latitudeSpan = bounds.maxLatitude - bounds.minLatitude || 1;
  const availableWidth = DISTRICT_MAP_WIDTH - DISTRICT_MAP_PADDING * 2;
  const availableHeight = DISTRICT_MAP_HEIGHT - DISTRICT_MAP_PADDING * 2;
  const scale = Math.min(availableWidth / longitudeSpan, availableHeight / latitudeSpan);
  const offsetX = (DISTRICT_MAP_WIDTH - longitudeSpan * scale) / 2;
  const offsetY = (DISTRICT_MAP_HEIGHT - latitudeSpan * scale) / 2;

  return ([longitude, latitude]) => ({
    x: offsetX + (longitude - bounds.minLongitude) * scale,
    y: offsetY + (bounds.maxLatitude - latitude) * scale,
  });
};

const getPointCenter = (points) => {
  if (points.length === 0) {
    return { x: DISTRICT_MAP_WIDTH / 2, y: DISTRICT_MAP_HEIGHT / 2 };
  }

  const bounds = points.reduce(
    (currentBounds, point) => ({
      minX: Math.min(currentBounds.minX, point.x),
      maxX: Math.max(currentBounds.maxX, point.x),
      minY: Math.min(currentBounds.minY, point.y),
      maxY: Math.max(currentBounds.maxY, point.y),
    }),
    {
      minX: Infinity,
      maxX: -Infinity,
      minY: Infinity,
      maxY: -Infinity,
    }
  );

  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
};

const ringToPath = (ring, project) => {
  if (!Array.isArray(ring)) {
    return "";
  }

  const commands = ring
    .map((point, index) => {
      const { x, y } = project(point);
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .filter(Boolean);

  return commands.length > 0 ? `${commands.join(" ")} Z` : "";
};

const geometryToPath = (geometry, project) => {
  if (!geometry) return "";

  if (geometry.type === "Polygon") {
    return geometry.coordinates
      .map((ring) => ringToPath(ring, project))
      .filter(Boolean)
      .join(" ");
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates
      .flatMap((polygon) => polygon.map((ring) => ringToPath(ring, project)))
      .filter(Boolean)
      .join(" ");
  }

  if (geometry.type === "GeometryCollection") {
    return (geometry.geometries || []).map((item) => geometryToPath(item, project)).join(" ");
  }

  return "";
};

export const getDistrictPaths = (geoJson) => {
  const features = Array.isArray(geoJson?.features) ? geoJson.features : [];
  const allPoints = features.flatMap((feature) => getGeometryPoints(feature.geometry));

  if (allPoints.length === 0) {
    return [];
  }

  const project = getProjection(allPoints);

  return features
    .map((feature, index) => {
      const name = getDistrictName(feature.properties, index);
      const featurePoints = getGeometryPoints(feature.geometry).map(project);

      return {
        id: feature.id || `${name}-${index}`,
        name,
        d: geometryToPath(feature.geometry, project),
        labelPoint: getPointCenter(featurePoints),
      };
    })
    .filter((district) => district.d);
};

const DistrictMap = ({ districts = [], stateName, status }) => {
  if (districts.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center text-center text-sm font-semibold text-[#1e3a5f]">
        {status === "loading" ? "Loading district map..." : "District map is not available"}
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${DISTRICT_MAP_WIDTH} ${DISTRICT_MAP_HEIGHT}`}
      className="absolute inset-0 h-full w-full"
      role="img"
      aria-label={`${stateName} district map`}
    >
      {districts.map((district) => (
        <path
          key={district.id}
          d={district.d}
          fill={DISTRICT_FILL_COLOR}
          fillRule="evenodd"
          clipRule="evenodd"
          stroke={DISTRICT_STROKE_COLOR}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          pointerEvents="none"
        />
      ))}
    </svg>
  );
};

export default DistrictMap;
